// CancelSubscriptionModal Component
// Confirmation dialog for cancelling a subscription

import { useState } from 'react';
import { FiAlertTriangle, FiCalendar, FiPackage } from 'react-icons/fi';
import { FormModal, FormField, Textarea } from '../../ui';
import styles from './CancelSubscriptionModal.module.css';

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (cancelAtPeriodEnd: boolean, reason?: string) => void;
  loading: boolean;
  currentPlan: string;
  periodEnd?: string;
}

export function CancelSubscriptionModal({
  isOpen,
  onClose,
  onConfirm,
  loading,
  currentPlan,
  periodEnd,
}: CancelSubscriptionModalProps) {
  const [reason, setReason] = useState('');
  const [cancelImmediately, setCancelImmediately] = useState(false);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'end of billing period';
    try {
      return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return 'end of billing period';
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;
    onConfirm(!cancelImmediately, reason || undefined);
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Cancel Subscription?"
      size="medium"
      onSubmit={handleSubmit}
      submitLabel={loading ? 'Cancelling...' : 'Cancel Subscription'}
      cancelLabel="Keep Subscription"
      submitVariant="error"
      isSubmitting={loading}
    >
      <div className={styles.iconWrapper}>
        <FiAlertTriangle className={styles.icon} />
      </div>

      <p className={styles.message}>
        Are you sure you want to cancel your <strong>{currentPlan}</strong> plan?
      </p>

      <div className={styles.infoBox}>
        <div className={styles.infoRow}>
          <FiCalendar className={styles.infoIcon} />
          <span>
            {cancelImmediately
              ? 'Your plan will be downgraded to Free immediately.'
              : <>You'll keep access until <strong>{formatDate(periodEnd)}</strong>, then be downgraded to Free.</>}
          </span>
        </div>
        <div className={styles.infoRow}>
          <FiPackage className={styles.infoIcon} />
          <span>Your remaining credits will be available until the plan ends.</span>
        </div>
      </div>

      <label className={styles.checkboxLabel}>
        <input
          type="checkbox"
          checked={cancelImmediately}
          onChange={(e) => setCancelImmediately(e.target.checked)}
          className={styles.checkbox}
        />
        <span>Cancel immediately (lose remaining time)</span>
      </label>

      <FormField label="Reason for cancelling (optional)" htmlFor="cancel-reason">
        <Textarea
          id="cancel-reason"
          placeholder="Tell us why you're leaving..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
        />
      </FormField>
    </FormModal>
  );
}
